import { CertificationItem } from 'types/api/CertificationItemProductKey';
import I18n from 'i18n-js';

// Known age level substrings used to parse the age level from a combined
// certification name string (e.g. "CLASS 2nd Edition Infant-Toddler").
// getAgeLevelGroup() uses .find() against this list, so ORDER MATTERS:
// combined entries like 'Infant-Toddler' must come before their individual
// parts ('Infant', 'Toddler') to avoid partial substring matches.
const ageLevelGroups = [
  'Infant, Toddler',
  'Infant-Toddler',
  'Infant',
  'Toddler',
  'Pre-K + K-3',
  'Pre-K–3rd',
  'Pre-K',
  'K-3',
  'Upper Elementary',
  'Secondary'
];

// Defines the display sort order when a user has multiple renewable
// certifications. Position in this array determines the order shown in the UI.
export const ageLevelsOrder = [
  'CLASS 2nd Edition Infant-Toddler',
  'CLASS 2008 Infant',
  'CLASS 2008 Toddler',
  'CLASS 2nd Edition Pre-K',
  'CLASS 2008 Pre-K',
  'CLASS 2nd Edition Pre-K–3rd',
  'CLASS 2008 K-3',
  'CLASS 2008 Upper Elementary',
  'CLASS 2008 Secondary'
];

export function formatCertification(selectedCertification: CertificationItem) {
  const hasStructuredCertifications =
    Array.isArray(selectedCertification.certifications) &&
    selectedCertification.certifications.length > 0;
  const hasLegacyMultipleCertifications =
    Array.isArray(selectedCertification.version) &&
    selectedCertification.multiple_certifications;

  return hasStructuredCertifications
    ? formatStructuredCertifications(selectedCertification)
    : hasLegacyMultipleCertifications
      ? formatLegacyMultipleCertifications(selectedCertification)
      : formatSingleCertification(selectedCertification);
}

// Render straight from the structured backend lines - each line's own `name` is
// a clean single certification, unlike the top-level name that joins every line.
function formatStructuredCertifications(
  selectedCertification: CertificationItem
) {
  return selectedCertification.certifications!.map((cert, index) => {
    const ageLevelGroup = getSingleAgeLevelGroup(cert.name);
    const version = cert.name.replace(ageLevelGroup, '').trim();

    return (
      <div key={index}>
        {`${version} `}
        <span className="font-semibold whitespace-nowrap">{ageLevelGroup}</span>
        <div className="text-base" style={{ color: '#5C5C5C' }}>
          {cert.label}
        </div>
        {index < selectedCertification.certifications!.length - 1 && (
          <div>{'+'}</div>
        )}
      </div>
    );
  });
}

function formatLegacyMultipleCertifications(
  selectedCertification: CertificationItem
) {
  return (selectedCertification.version as string[]).map(
    (_: string, index: number) => (
      <div key={index}>
        {`${selectedCertification.version[index]} `}
        <span className="font-semibold whitespace-nowrap">
          {selectedCertification.age_level_group[index]}
        </span>
        <div className="text-base" style={{ color: '#5C5C5C' }}>
          {`${selectedCertification.group.split('and')[index]}`}
        </div>
        {index === 0 ? <div>{'+'}</div> : <></>}
      </div>
    )
  );
}

function formatSingleCertification(selectedCertification: CertificationItem) {
  return (
    <>
      {`${selectedCertification.version} `}
      <span className="font-semibold whitespace-nowrap">
        {selectedCertification.age_level_group}
      </span>
      <div className="text-base" style={{ color: '#5C5C5C' }}>
        {selectedCertification.group}
      </div>
    </>
  );
}

// The backend's structured `certifications` array is the reliable source of
// truth for whether an option bundles more than one certification - the
// combined `name` string (joined with "+") can't be safely re-parsed for this.
export function isMultipleCertifications(
  certification: Pick<CertificationItem, 'certifications'> &
    Partial<Pick<CertificationItem, 'name'>>
) {
  if (certification.certifications)
    return certification.certifications.length > 1;

  return certification.name?.includes('and') ?? false;
}

// A recertification and a transition option (or two different bundle
// combinations) can share the same test_template_id and role, so those two
// fields alone don't uniquely identify an option. This key adds `type` and
// the bundle's own certification ids to disambiguate them.
export function getCertificationKey(
  certification: Pick<
    CertificationItem,
    'test_template_id' | 'role' | 'bundled_user_certification_ids'
  >,
  type: 'recertification' | 'transition'
) {
  const bundleKey = (certification.bundled_user_certification_ids ?? [])
    .slice()
    .sort((a, b) => a - b)
    .join(',');

  return `${type}:${certification.test_template_id}:${certification.role}:${bundleKey}`;
}

// Like getCertificationKey, but excludes test_template_id, which differs per
// language - used to find the same option in a different language.
export function getCertificationSignature(
  certification: Pick<
    CertificationItem,
    'name' | 'role' | 'bundled_user_certification_ids'
  >
) {
  const bundleKey = (certification.bundled_user_certification_ids ?? [])
    .slice()
    .sort((a, b) => a - b)
    .join(',');

  return `${certification.name}:${certification.role}:${bundleKey}`;
}

export function getSingleAgeLevelGroup(name: string) {
  const ageLevelGroup = ageLevelGroups.find(group => name.includes(group));

  if (!ageLevelGroup) {
    return '';
  }

  return ageLevelGroup;
}

/** We can have more than one certification
 * for example: CLASS 2008 Pre-K and CLASS 2nd Edition Pre-K-3rd
 * returns: ['CLASS 2008', 'CLASS 2nd Edition']
 */
export function getVersions(name: string) {
  if (name.includes('and')) {
    const versions = name.split('and');
    versions.forEach((item: string, index: number) => {
      versions[index] = item.replace(getSingleAgeLevelGroup(item), '').trim();
    });

    return versions;
  }

  return name.replace(getSingleAgeLevelGroup(name), '').trim();
}

/** We can have more than one certification
 * for example: CLASS 2008 Pre-K and CLASS 2nd Edition Pre-K-3rd
 * returns: ['Pre-K', 'Pre-K-3rd']
 */
export function getAgeLevelGroup(name: string) {
  if (name.includes('and')) {
    let ageLevelGroupArray: string[] = [];
    name.split('and').forEach((item: string) => {
      const ageLevelGroup = ageLevelGroups.find(ageLevelGroup =>
        item.includes(ageLevelGroup)
      );
      if (ageLevelGroup) ageLevelGroupArray.push(ageLevelGroup);
    });

    return ageLevelGroupArray || '';
  }

  const ageLevelGroup = ageLevelGroups.find(ageLevelGroup =>
    name.includes(ageLevelGroup)
  );

  return ageLevelGroup || '';
}

export function getAgeLevelIndex(certification: CertificationItem): any {
  return ageLevelsOrder.indexOf(
    normalizeCombinedAgeLevel(
      certification.certifications?.[0]?.name ?? certification.name
    )
  );
}

export function sortByAgeLevelsOrder(
  a: CertificationItem,
  b: CertificationItem
) {
  return getAgeLevelIndex(a) - getAgeLevelIndex(b);
}

// Display order for the 2nd edition age level group headers options are grouped
// under, regardless of an individual option's own edition.
export const anchorAgeLevelGroupOrder = [
  'Infant-Toddler',
  'Pre-K',
  'Pre-K–3rd',
  'Upper Elementary',
  'Secondary'
];

export function getAnchorAgeLevelGroupIndex(certification: CertificationItem) {
  return anchorAgeLevelGroupOrder.indexOf(
    normalizeCombinedAgeLevel(certification.anchor_age_level_group ?? '')
  );
}

function normalizeCombinedAgeLevel(value: string) {
  return value.replace('Infant, Toddler', 'Infant-Toddler');
}

// Sorts by 2nd edition age level group family first (so options for the same
// family stay together for section headers), then by the existing age level order.
export function sortByAnchorAgeLevelGroup(
  a: CertificationItem,
  b: CertificationItem
) {
  const familyDiff =
    getAnchorAgeLevelGroupIndex(a) - getAnchorAgeLevelGroupIndex(b);

  return familyDiff !== 0 ? familyDiff : sortByAgeLevelsOrder(a, b);
}

// Formats the inserted certification-name list using the same locale as the
// surrounding translated warning (for example: "A and B", "A y B", "A et B").
export function joinWithAnd(
  names: string[],
  locale: string = I18n.currentLocale()
): string {
  return new Intl.ListFormat(locale, {
    style: 'long',
    type: 'conjunction'
  }).format(names);
}
